import React, { useState, useCallback, useEffect, useRef } from "react";
import "../../styles/HomeAboutStickManAnimation.css";
import { ReactTyped } from "react-typed";
import Lottie from "lottie-react";
import codingAnimation from "../../assets/LottieFiles/vishnusimha.json";
import {
  FaGithub,
  FaLinkedin,
  FaEnvelope,
  FaShieldAlt,
  FaSync,
  FaCalendarAlt,
  FaMapMarkerAlt,
  FaCode,
  FaHeart,
  FaCoffee,
  FaVideo,
  FaFileAlt,
  FaExternalLinkAlt,
  FaChevronLeft,
  FaChevronRight,
  FaGraduationCap,
  FaBriefcase,
  FaRocket,
  FaUniversity,
  FaPlane,
  FaCertificate,
} from "react-icons/fa";
import { SiLeetcode, SiHackerrank } from "react-icons/si";

const GITHUB_README_STATS_BASE_URL = "https://github-readme-stats.vercel.app";
const GITHUB_README_STATS_FALLBACK_URL =
  "https://github-readme-stats-sigma-five.vercel.app";
const emptyCertificationAssetContext = {
  keys: () => [],
};
const certificationImageContext =
  process.env.NODE_ENV === "test"
    ? emptyCertificationAssetContext
    : require.context(
        "../../assets/certifications",
        false,
        /\.(webp|png|jpe?g)$/,
      );
const certificationPdfContext =
  process.env.NODE_ENV === "test"
    ? emptyCertificationAssetContext
    : require.context("../../assets/certifications", false, /\.pdf$/);

const getCertificationAsset = (assetContext, filename) => {
  if (!filename) {
    return null;
  }

  const assetKey = `./${filename}`;
  return assetContext.keys().includes(assetKey) ? assetContext(assetKey) : null;
};

const getCertificationCardsPerPage = () => {
  if (typeof window === "undefined" || !window.matchMedia) {
    return 6;
  }

  if (window.matchMedia("(max-width: 480px)").matches) {
    return 1;
  }

  if (window.matchMedia("(max-width: 768px)").matches) {
    return 2;
  }

  if (window.matchMedia("(max-width: 1024px)").matches) {
    return 4;
  }

  return 6;
};

const certificationTabs = [
  { id: "technical", label: "Technical" },
  { id: "professional", label: "Professional Skills" },
  { id: "all", label: "All" },
];

const GitHubStatsCard = ({
  src,
  fallbackSrc,
  alt,
  title,
  href,
  className = "",
  isRefreshing,
  onLoadError,
}) => {
  const imageRef = useRef(null);
  const [activeSrc, setActiveSrc] = useState(src);
  const [loadState, setLoadState] = useState("loading");

  useEffect(() => {
    setActiveSrc(src);
    setLoadState("loading");
  }, [src]);

  useEffect(() => {
    const image = imageRef.current;
    if (!isRefreshing && image?.complete && image.naturalWidth > 0) {
      setLoadState("loaded");
    }
  }, [activeSrc, isRefreshing]);

  const handleImageError = () => {
    if (fallbackSrc && activeSrc !== fallbackSrc) {
      setActiveSrc(fallbackSrc);
      setLoadState("loading");
      return;
    }

    setLoadState("failed");
    onLoadError(alt);
  };

  if (loadState === "failed") {
    return null;
  }

  return (
    <div className={`github-stats-container ${className}`}>
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        title={title}
        aria-label={`View ${title} on GitHub`}
      >
        {(isRefreshing || loadState === "loading") && (
          <div
            className="stats-loading-skeleton"
            role="img"
            aria-label="Loading GitHub statistics"
          >
            <div className="skeleton-content"></div>
          </div>
        )}
        {!isRefreshing && (
          <img
            ref={imageRef}
            src={activeSrc}
            alt={alt}
            className={`github-stats-image ${
              loadState === "loaded" ? "" : "loading"
            }`}
            loading="lazy"
            onLoad={() => setLoadState("loaded")}
            onError={handleImageError}
          />
        )}
      </a>
    </div>
  );
};

const CredlyBadge = ({ badgeId, title }) => {
  const badgeRef = useRef(null);

  useEffect(() => {
    if (!badgeRef.current || !badgeId) {
      return undefined;
    }

    badgeRef.current.innerHTML = "";

    const badgeContainer = document.createElement("div");
    badgeContainer.setAttribute("data-iframe-width", "150");
    badgeContainer.setAttribute("data-iframe-height", "270");
    badgeContainer.setAttribute("data-share-badge-id", badgeId);
    badgeContainer.setAttribute(
      "data-share-badge-host",
      "https://www.credly.com",
    );

    const embedScript = document.createElement("script");
    embedScript.src = "https://cdn.credly.com/assets/utilities/embed.js";
    embedScript.async = true;
    embedScript.type = "text/javascript";

    badgeRef.current.appendChild(badgeContainer);
    badgeRef.current.appendChild(embedScript);

    const currentRef = badgeRef.current;
    return () => {
      if (currentRef) {
        currentRef.innerHTML = "";
      }
    };
  }, [badgeId]);

  return (
    <div
      className="credly-badge-slot"
      ref={badgeRef}
      aria-label={title}
      title={title}
    />
  );
};

const Home = React.forwardRef((props, ref) => {
  // Constants
  const REFRESH_DELAY = 1000;

  const [refreshKey, setRefreshKey] = useState(0);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [showJourneyStickman, setShowJourneyStickman] = useState(true);

  const toggleJourneyStickman = useCallback(() => {
    setShowJourneyStickman((prev) => !prev);
  }, []);

  const handleJourneyStickmanKeyDown = useCallback((event) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      setShowJourneyStickman((prev) => !prev);
    }
  }, []);

  const githubStatsQuery =
    "username=Vishnusimha&theme=dark&hide_border=false&include_all_commits=true&count_private=true";
  const githubTopLanguagesQuery =
    "username=Vishnusimha&theme=dark&hide_border=false&layout=compact";

  const handleRefreshStats = useCallback(() => {
    setIsRefreshing(true);
    setError(null);
    setRefreshKey((prev) => prev + 1);

    // Simulate refresh delay
    setTimeout(() => {
      setIsRefreshing(false);
    }, REFRESH_DELAY);
  }, [REFRESH_DELAY]);

  const socialLinks = [
    {
      icon: <FaGithub />,
      url: "https://github.com/Vishnusimha",
      label: "GitHub Profile",
    },
    {
      icon: <FaEnvelope />,
      url: "mailto:vishnusimha98@gmail.com",
      label: "Email Contact",
    },
    {
      icon: <FaLinkedin />,
      url: "https://www.linkedin.com/in/vishnusimhadussa/",
      label: "LinkedIn Profile",
    },
    {
      icon: <SiLeetcode />,
      url: "https://leetcode.com/u/vishnusimha98/",
      label: "LeetCode Profile",
    },
    {
      icon: <SiHackerrank />,
      url: "https://www.hackerrank.com/profile/vishnusimha98",
      label: "HackerRank Profile",
    },
    {
      icon: <FaShieldAlt />,
      url: "https://www.credly.com/users/vishnu-simha-dussa/",
      label: "Credly Certifications",
    },
  ];

  const credlyUrl = "https://www.credly.com/users/vishnu-simha-dussa/";
  const resumeDriveFolderUrl =
    "https://drive.google.com/drive/folders/1NlW3fx6zGikIgl43u6mGzre9b4BJqRYw?usp=drive_link";

  const trustHighlights = [
    { value: "4+ Years", label: "Android & Software Engineering" },
    {
      value: "HID Global",
      label: "Enterprise Mobile Access Experience",
      url: "https://www.hidglobal.com/",
      ariaLabel: "Visit HID Global official website",
    },
    {
      value: "DCU🎓",
      label:
        "MSc Electronic & Computer Technology (IoT) · Honors 2:1 · Dublin, Ireland 🇮🇪",
      url: "https://www.dcu.ie/",
      ariaLabel: "Visit Dublin City University official website",
    },
    {
      value: "AWS",
      label: "Certified Solutions Architect - Associate + Cloud Practitioner",
      url: credlyUrl,
      ariaLabel: "View Vishnu Simha Dussa AWS certifications on Credly",
    },
    {
      value: "Google Play",
      label: "Published 2 Android Apps",
      url: "https://play.google.com/store/apps/developer?id=Simha+Tech+Studio",
      ariaLabel: "View Simha Tech Studio apps on Google Play",
    },
    // { value: "SaaS", label: "Full Stack Product Development" },
  ];

  const awsCredentialBadges = [
    {
      id: "15b8ad98-f4c4-41e0-8312-2391645d10ee",
      title: "AWS Certified Solutions Architect - Associate",
    },
    {
      id: "7fca815e-0c82-4ad4-86cf-bc9369337ff5",
      title: "AWS Certified Cloud Practitioner",
    },
  ];

const certifications = [
  // TODO: Add thumbnail files in src/assets/certifications/ using these exact filenames.
  // Optional PDFs can live in the same folder and be referenced with localPdfFilename.

  {
    title: "AWS Certified Solutions Architect - Associate",
    issuer: "Amazon Web Services",
    issuedDate: "Issued 2024",
    imageFilename: "aws-solutions-architect.webp",
    credentialUrl: credlyUrl,
    description:
      "Resilient AWS architecture, networking, storage, compute, and cost-aware design.",
    category: "technical",
    type: "Technical",
    skills: ["Cloud", "Architecture", "Networking"],
  },
  {
    title: "AWS Certified Cloud Practitioner",
    issuer: "Amazon Web Services",
    issuedDate: "Issued 2024",
    imageFilename: "aws-cloud-practitioner.webp",
    credentialUrl: credlyUrl,
    description:
      "Cloud fundamentals, IAM, billing, security, and AWS core services.",
    category: "technical",
    type: "Technical",
    skills: ["Cloud", "IAM", "AWS"],
  },
  {
    title: "What Is Generative AI?",
    issuer: "LinkedIn Learning",
    issuedDate: "Issued May 2026",
    imageFilename: "what-is-generative-ai.webp",
    credentialUrl: "",
    localPdfFilename: "",
    description:
      "Completed LinkedIn Learning training covering generative AI concepts, tools, and practical AI use cases.",
    category: "technical",
    type: "Technical",
    skills: ["AI", "Generative AI", "AI Tools"],
  },
  {
    title: "Career Essentials in GitHub Professional Certificate",
    issuer: "GitHub",
    issuedDate: "Issued 2025",
    imageFilename: "github-career-essentials-professional-certificate.webp",
    credentialUrl: "",
    description:
      "Professional GitHub skills including version control, repositories, collaboration workflows, and GitHub best practices.",
    category: "technical",
    type: "Technical",
    skills: ["GitHub", "Git", "Version Control"],
  },
  {
    title: "Kubernetes for the Absolute Beginners",
    issuer: "Udemy",
    issuedDate: "Issued 2024",
    imageFilename: "kubernetes-udemy.webp",
    description:
      "Kubernetes fundamentals, pods, deployments, services, and cluster basics.",
    category: "technical",
    type: "Technical",
    skills: ["Kubernetes", "Containers", "DevOps"],
  },
  {
    title: "Docker for Java Developers",
    issuer: "Udemy",
    issuedDate: "Issued 2024",
    imageFilename: "docker-java.webp",
    description:
      "Containerized Java workflows, Docker images, Compose, and developer environments.",
    category: "technical",
    type: "Technical",
    skills: ["DevOps", "Java", "Containers"],
  },
  {
    title: "Learning Spring with Spring Boot",
    issuer: "LinkedIn Learning",
    issuedDate: "Issued 2024",
    imageFilename: "learning-spring-with-spring-boot.webp",
    credentialUrl: "",
    description:
      "Spring Boot fundamentals including application setup, REST APIs, dependency injection, and backend development.",
    category: "technical",
    type: "Technical",
    skills: ["Backend", "Java", "REST APIs"],
  },
  {
    title: "Spring Data",
    issuer: "LinkedIn Learning",
    issuedDate: "Issued 2024",
    imageFilename: "spring-data.webp",
    credentialUrl: "",
    description:
      "Spring Data concepts including repositories, database access, JPA integration, and data persistence patterns.",
    category: "technical",
    type: "Technical",
    skills: ["Backend", "JPA", "Databases"],
  },
  {
    title: "Spring: Spring Security",
    issuer: "LinkedIn Learning",
    issuedDate: "Issued 2024",
    imageFilename: "spring-security.webp",
    credentialUrl: "",
    description:
      "Spring Security fundamentals including authentication, authorization, secure endpoints, and application security.",
    category: "technical",
    type: "Technical",
    skills: ["Backend", "Security", "Authentication"],
  },

  {
    title: "Java Advanced Concepts for High-Performance Development",
    issuer: "LinkedIn Learning",
    issuedDate: "Issued 2024",
    imageFilename: "java-advanced-high-performance-development.webp",
    credentialUrl: "",
    description:
      "Advanced Java development concepts focused on performance, maintainability, and efficient application design.",
    category: "technical",
    type: "Technical",
    skills: ["Backend", "Java", "Performance"],
  },
  {
    title:
      "Advanced Java: Streams, Lambda Expressions, Collections, Generics and More",
    issuer: "LinkedIn Learning",
    issuedDate: "Issued 2024",
    imageFilename: "advanced-java-streams-lambda-collections-generics.webp",
    credentialUrl: "",
    description:
      "Hands-on advanced Java concepts including Streams, Lambda Expressions, Collections, and Generics.",
    category: "technical",
    type: "Technical",
    skills: ["Backend", "Java", "Streams", "Generics"],
  },
  {
    title: "Advance Your Java Skills",
    issuer: "LinkedIn Learning",
    issuedDate: "Issued 2024",
    imageFilename: "advance-your-java-skills.webp",
    credentialUrl: "",
    description:
      "Advanced Java programming concepts for improving code quality, readability, and problem-solving skills.",
    category: "technical",
    type: "Technical",
    skills: ["Backend", "Java", "OOP"],
  },

  {
    title: "Introduction to PostgreSQL",
    issuer: "LinkedIn Learning",
    issuedDate: "Issued 2024",
    imageFilename: "introduction-to-postgresql.webp",
    credentialUrl: "",
    description:
      "PostgreSQL fundamentals including relational database concepts, SQL queries, schema design, and database operations.",
    category: "technical",
    type: "Technical",
    skills: ["Backend", "PostgreSQL", "Databases"],
  },
  {
    title: "Data Science for Java Developers",
    issuer: "LinkedIn Learning",
    issuedDate: "Issued May 2024",
    imageFilename: "data-science-for-java-developers.webp",
    credentialUrl: "",
    category: "technical",
    type: "Technical",
    description:
      "Completed training covering data science concepts for Java developers, including practical Java-based data analysis foundations.",
    skills: ["Java", "Data Science"],
  },
  {
    title: "SQL Certificate",
    issuer: "Training Certificate",
    issuedDate: "Issued 2024",
    imageFilename: "sql-certificate.webp",
    description:
      "Relational database fundamentals, SQL querying, joins, filtering, and data modeling.",
    category: "technical",
    type: "Technical",
    skills: ["Backend", "SQL", "Databases"],
  },
  {
    title: "C & C++ Programming",
    issuer: "SRM Axis Intellects",
    issuedDate: "Issued July 2016",
    imageFilename: "c-and-cpp-certificate.webp",
    credentialUrl: "",
    localPdfFilename: "",
    description:
      "Completed foundational programming training in C and C++ during undergraduate studies.",
    category: "technical",
    type: "Technical",
    skills: ["Programming", "C", "C++"],
  },
  {
    title: "Basics of OrCAD",
    issuer: "ProCAE / HEXDOF Engineering Services Pvt. Ltd.",
    issuedDate: "Issued Mar 2017",
    imageFilename: "basic-of-orcad-certificate.webp",
    credentialUrl: "",
    localPdfFilename: "",
    description:
      "Completed a 40-hour training course covering the basics of OrCAD for electronics and circuit design.",
    category: "technical",
    type: "Technical",
    skills: ["OrCAD", "Circuit Design", "Electronics"],
  },
  {
    title: "Negotiation Professional Certificate",
    issuer: "LinkedIn Learning / American Negotiation Institute",
    issuedDate: "Issued Jun 2025",
    imageFilename:
      "negotiation-professional-certificate-american-negotiation-institute.webp",
    credentialUrl: "",
    category: "professional",
    type: "Professional Skill",
    description:
      "Completed a professional negotiation learning path covering negotiation strategy, interpersonal skills, and structured negotiation techniques.",
    skills: ["Negotiation", "Strategic Negotiations", "Interpersonal Skills"],
  },
  {
    title: "Negotiation Foundations",
    issuer: "LinkedIn Learning",
    issuedDate: "Issued Jun 2025",
    imageFilename: "negotiation-foundations.webp",
    credentialUrl: "",
    category: "professional",
    type: "Professional Skill",
    description:
      "Completed foundational negotiation training covering core negotiation principles, preparation, and communication techniques.",
    skills: ["Negotiation", "Communication"],
  },
  {
    title: "Negotiating with Agility",
    issuer: "LinkedIn Learning",
    issuedDate: "Issued Jun 2025",
    imageFilename: "negotiating-with-agility.webp",
    credentialUrl: "",
    category: "professional",
    type: "Professional Skill",
    description:
      "Completed training focused on adapting negotiation style, responding to changing situations, and negotiating with flexibility.",
    skills: ["Negotiation", "Agility", "Communication"],
  },
  {
    title: "Mindsets and Strategies for Negotiation Success",
    issuer: "LinkedIn Learning",
    issuedDate: "Issued Apr 2024",
    imageFilename: "mindsets-strategies-negotiation-success.webp",
    credentialUrl: "",
    category: "professional",
    type: "Professional Skill",
    description:
      "Completed training on negotiation mindset, preparation strategies, and practical approaches for successful negotiation outcomes.",
    skills: ["Negotiation", "Strategy", "Professional Communication"],
  },
  {
    title: "Building Better Relationships through Listening and Validation",
    issuer: "LinkedIn Learning",
    issuedDate: "Issued Jun 2025",
    imageFilename: "building-better-relationships-listening-validation.webp",
    credentialUrl: "",
    category: "professional",
    type: "Professional Skill",
    description:
      "Completed training focused on active listening, validation, relationship building, and stronger workplace communication.",
    skills: ["Relationship Building", "Active Listening", "Communication"],
  },
  {
    title: "Developing Your Emotional Intelligence",
    issuer: "LinkedIn Learning",
    issuedDate: "Issued Apr 2024",
    imageFilename: "developing-your-emotional-intelligence.webp",
    credentialUrl: "",
    category: "professional",
    type: "Professional Skill",
    description:
      "Completed training covering emotional intelligence, self-awareness, empathy, and professional relationship management.",
    skills: ["Emotional Intelligence", "Self-Awareness", "Empathy"],
  },
  {
    title: "Speaking Confidently and Effectively",
    issuer: "LinkedIn Learning",
    issuedDate: "Issued Apr 2024",
    imageFilename: "speaking-confidently-effectively.webp",
    credentialUrl: "",
    category: "professional",
    type: "Professional Skill",
    description:
      "Completed training focused on confident speaking, presentation skills, and effective professional communication.",
    skills: ["Public Speaking", "Presentation Skills", "Communication"],
  },
  {
    title: "How to Speak Smarter When Put on the Spot",
    issuer: "LinkedIn Learning",
    issuedDate: "Issued Apr 2024",
    imageFilename: "how-to-speak-smarter-when-put-on-the-spot.webp",
    credentialUrl: "",
    category: "professional",
    type: "Professional Skill",
    description:
      "Completed training on thinking clearly under pressure, responding effectively, and communicating with confidence in spontaneous situations.",
    skills: ["Public Speaking", "Professional Communication", "Confidence"],
  },
  {
    title: "Expert Tips for Answering Common Interview Questions",
    issuer: "LinkedIn Learning",
    issuedDate: "Issued May 2024",
    imageFilename: "expert-tips-answering-common-interview-questions.webp",
    credentialUrl: "",
    category: "professional",
    type: "Professional Skill",
    description:
      "Completed training focused on interview preparation, structuring responses, and communicating professional experience effectively.",
    skills: ["Interview Preparation", "Communication", "Career Development"],
  },
  {
    title: "When To Be Assertive",
    issuer: "GLU / BrightHR",
    issuedDate: "Issued Mar 2025",
    imageFilename: "when-to-be-assertive.webp",
    credentialUrl: "",
    category: "professional",
    type: "Professional Skill",
    description:
      "Completed workplace training covering assertive communication, confidence, and professional boundary-setting.",
    skills: ["Assertiveness", "Communication", "Workplace Skills"],
  },
  {
    title: "Resilience",
    issuer: "BrightHR",
    issuedDate: "Issued Feb 2025",
    imageFilename: "resilience.webp",
    credentialUrl: "",
    category: "professional",
    type: "Professional Skill",
    description:
      "Completed workplace training focused on resilience, adaptability, and maintaining effectiveness in challenging situations.",
    skills: ["Resilience", "Adaptability", "Workplace Skills"],
  },
  {
    title: "Risk Assessment",
    issuer: "BrightSafe / BrightHR",
    issuedDate: "Issued Feb 2025",
    imageFilename: "risk-assessment.webp",
    credentialUrl: "",
    category: "professional",
    type: "Professional Skill",
    description:
      "Completed workplace training covering risk assessment awareness, hazard identification, and safer working practices.",
    skills: ["Risk Assessment", "Safety Awareness", "Workplace Safety"],
  },
];

  const currentFocusItems = [
    "AppointmentHub SaaS platform",
    "Technical blogging and quiz platform",
    "Cloud deployment and CI/CD workflows",
  ];

  const engineeringFocus = [
    {
      title: "Android Engineering",
      description:
        "Kotlin, Jetpack Compose, MVVM, Coroutines, Room, Firebase, BLE/NFC, Play Store releases.",
    },
    {
      title: "Backend Systems",
      description:
        "Spring Boot, REST APIs, SQL, authentication flows, clean architecture, and scalable business logic.",
    },
    {
      title: "Cloud & DevOps",
      description:
        "AWS, Docker, GitHub Actions, Jenkins, SonarQube, deployment workflows, and production readiness.",
    },
    {
      title: "Product Engineering",
      description:
        "End-to-end ownership from requirements, UX, implementation, testing, release, and maintenance.",
    },
  ];

  const featuredProjects = [
    {
      title: "AppointmentHub SaaS Platform",
      subtitle: "Full stack appointment booking platform",
      description:
        "A production-style booking system built as a real SaaS product showcase across frontend, backend, database design, authentication, seller dashboards, public booking flows, admin workflows, and deployment readiness.",
      highlights: [
        "Role-based React dashboards for seller, customer, and admin workflows",
        "Spring Boot REST APIs with authentication and business rules",
        "MySQL scheduling model designed for real booking scenarios",
        "Vercel, Docker, AWS, and CI/CD oriented deployment strategy",
      ],
      stack: [
        "React",
        "Spring Boot",
        "MySQL",
        "Docker",
        "AWS",
        "GitHub Actions",
      ],
      primaryAction: {
        label: "Live App",
        url: "https://market.simhatech.xyz/",
      },
      secondaryAction: {
        label: "View Project",
        type: "projects",
      },
      proof:
        "Shows end-to-end SaaS capability across UI, APIs, database design, authentication, deployment, dashboards, and product workflows.",
    },
    {
      title: "StocKeeper",
      subtitle: "Android stock management app",
      description:
        "A Play Store released inventory companion for tracking household and small-business stock, quick additions, saved shopping plans, alerts, and tablet-friendly layouts.",
      highlights: [
        "Jetpack Compose UI with modern Material-driven flows",
        "Firebase-backed user data and app state",
        "Room and DataStore patterns for structured local data handling",
        "Designed for fast stock lookup, alerts, and shopping planning",
      ],
      stack: ["Kotlin", "Compose", "Firebase", "Room", "Hilt", "Play Store"],
      primaryAction: {
        label: "Play Store",
        url: "https://play.google.com/store/apps/details?id=com.vishnu.stockeeper",
      },
      secondaryAction: {
        label: "GitHub",
        url: "https://github.com/Vishnusimha/StocKeeper",
      },
      proof:
        "Demonstrates production Android delivery, state management, persistence, release readiness, and real user-facing product thinking.",
    },
    {
      title: "VigilCam",
      subtitle: "Background video and audio recorder",
      description:
        "A Play Store released Android app focused on reliable background recording with foreground services, notification controls, secure media handling, and responsive mobile/tablet screens.",
      highlights: [
        "Foreground service architecture for long-running recording flows",
        "CameraX, MediaRecorder, and notification control integration",
        "Crashlytics-oriented reliability and release monitoring",
        "Compose-based UI designed for mobile and tablet usage",
      ],
      stack: [
        "Kotlin",
        "Compose",
        "CameraX",
        "MediaRecorder",
        "Crashlytics",
        "Play Store",
      ],
      primaryAction: {
        label: "Play Store",
        url: "https://play.google.com/store/apps/details?id=com.vishnu.vigilcam",
      },
      secondaryAction: {
        label: "GitHub",
        url: "https://github.com/Vishnusimha/VigilCam",
      },
      proof:
        "Highlights Android platform depth across background execution, media APIs, notifications, reliability, and Play Store release workflow.",
    },
  ];

  const [activeFeaturedProject, setActiveFeaturedProject] = useState(0);
  const featuredScrollRef = useRef(null);
  const [activeCertificationCategory, setActiveCertificationCategory] =
    useState("technical");
  const [activeCertificationPage, setActiveCertificationPage] = useState(0);
  const [failedCertificationImages, setFailedCertificationImages] = useState(
    {},
  );
  const [certificationCardsPerPage, setCertificationCardsPerPage] = useState(
    getCertificationCardsPerPage,
  );
  const certificationsScrollRef = useRef(null);
  const certificationDragRef = useRef({
    isDragging: false,
    pointerId: null,
    startX: 0,
    lastX: 0,
  });

  const scrollToFeaturedProject = useCallback(
    (index) => {
      const scrollContainer = featuredScrollRef.current;
      if (!scrollContainer) return;

      const normalizedIndex = Math.max(
        0,
        Math.min(index, featuredProjects.length - 1),
      );
      const targetCard = scrollContainer.children[normalizedIndex];

      if (targetCard) {
        const targetLeft =
          targetCard.offsetLeft -
          scrollContainer.offsetLeft -
          (scrollContainer.clientWidth - targetCard.clientWidth) / 2;

        scrollContainer.scrollTo({
          left: targetLeft,
          behavior: "smooth",
        });
        setActiveFeaturedProject(normalizedIndex);
      }
    },
    [featuredProjects.length],
  );

  const handleFeaturedScroll = useCallback(() => {
    const scrollContainer = featuredScrollRef.current;
    if (!scrollContainer) return;

    const cards = Array.from(scrollContainer.children);
    const viewportCenter =
      scrollContainer.scrollLeft + scrollContainer.clientWidth / 2;

    const nearestIndex = cards.reduce((nearest, card, index) => {
      const cardCenter =
        card.offsetLeft - scrollContainer.offsetLeft + card.clientWidth / 2;
      const currentDistance = Math.abs(cardCenter - viewportCenter);
      const nearestCard = cards[nearest];
      const nearestCardCenter =
        nearestCard.offsetLeft -
        scrollContainer.offsetLeft +
        nearestCard.clientWidth / 2;
      const nearestDistance = Math.abs(nearestCardCenter - viewportCenter);
      return currentDistance < nearestDistance ? index : nearest;
    }, 0);

    setActiveFeaturedProject(nearestIndex);
  }, []);

  useEffect(() => {
    const handleCertificationResize = () => {
      setCertificationCardsPerPage(getCertificationCardsPerPage());
    };

    handleCertificationResize();
    window.addEventListener("resize", handleCertificationResize);

    return () => {
      window.removeEventListener("resize", handleCertificationResize);
    };
  }, []);

  const visibleCertifications =
    activeCertificationCategory === "all"
      ? certifications
      : certifications.filter(
          (certification) =>
            certification.category === activeCertificationCategory,
        );
  const shouldPaginateCertifications = visibleCertifications.length > 6;
  const certificationPages = [];
  const certificationPageSize = shouldPaginateCertifications
    ? certificationCardsPerPage
    : Math.max(visibleCertifications.length, 1);

  for (
    let pageStart = 0;
    pageStart < visibleCertifications.length;
    pageStart += certificationPageSize
  ) {
    certificationPages.push(
      visibleCertifications.slice(pageStart, pageStart + certificationPageSize),
    );
  }

  const showCertificationProgressNote = visibleCertifications.length <= 2;
  const certificationProgressNote =
    activeCertificationCategory === "professional" &&
    visibleCertifications.length === 0
      ? "More credentials being added."
      : "Actively learning — more certifications on the way.";

  const scrollToCertificationPage = useCallback(
    (index) => {
      const scrollContainer = certificationsScrollRef.current;
      if (!scrollContainer) return;

      const normalizedIndex = Math.max(
        0,
        Math.min(index, certificationPages.length - 1),
      );
      const targetPage = scrollContainer.children[normalizedIndex];

      if (targetPage) {
        scrollContainer.scrollTo({
          left: targetPage.offsetLeft - scrollContainer.offsetLeft,
          behavior: "smooth",
        });
        setActiveCertificationPage(normalizedIndex);
      }
    },
    [certificationPages.length],
  );

  useEffect(() => {
    if (activeCertificationPage > certificationPages.length - 1) {
      scrollToCertificationPage(certificationPages.length - 1);
    }
  }, [
    activeCertificationPage,
    certificationPages.length,
    scrollToCertificationPage,
  ]);

  const handleCertificationCategoryChange = (categoryId) => {
    if (categoryId === activeCertificationCategory) return;

    setActiveCertificationCategory(categoryId);
    setActiveCertificationPage(0);
    certificationsScrollRef.current?.scrollTo({
      left: 0,
      behavior: "auto",
    });
  };

  const handleCertificationImageError = (certificationTitle) => {
    setFailedCertificationImages((currentFailedImages) => ({
      ...currentFailedImages,
      [certificationTitle]: true,
    }));
  };

  const handleCertificationScroll = useCallback(() => {
    const scrollContainer = certificationsScrollRef.current;
    if (!scrollContainer) return;

    const pages = Array.from(scrollContainer.children);
    const viewportCenter =
      scrollContainer.scrollLeft + scrollContainer.clientWidth / 2;

    const nearestIndex = pages.reduce((nearest, page, index) => {
      const pageCenter =
        page.offsetLeft - scrollContainer.offsetLeft + page.clientWidth / 2;
      const currentDistance = Math.abs(pageCenter - viewportCenter);
      const nearestPage = pages[nearest];
      const nearestPageCenter =
        nearestPage.offsetLeft -
        scrollContainer.offsetLeft +
        nearestPage.clientWidth / 2;
      const nearestDistance = Math.abs(nearestPageCenter - viewportCenter);
      return currentDistance < nearestDistance ? index : nearest;
    }, 0);

    setActiveCertificationPage(nearestIndex);
  }, []);

  const handleCertificationPrevious = () => {
    const previousIndex =
      activeCertificationPage === 0
        ? certificationPages.length - 1
        : activeCertificationPage - 1;
    scrollToCertificationPage(previousIndex);
  };

  const handleCertificationNext = () => {
    const nextIndex =
      activeCertificationPage === certificationPages.length - 1
        ? 0
        : activeCertificationPage + 1;
    scrollToCertificationPage(nextIndex);
  };

  const resetCertificationDrag = (targetElement) => {
    certificationDragRef.current = {
      isDragging: false,
      pointerId: null,
      startX: 0,
      lastX: 0,
    };
    targetElement?.classList.remove("is-dragging");
  };

  const handleCertificationPointerDown = (event) => {
    if (!shouldPaginateCertifications) return;
    if (event.pointerType === "mouse" && event.button !== 0) return;
    if (event.target.closest("a, button")) return;

    certificationDragRef.current = {
      isDragging: false,
      pointerId: event.pointerId,
      startX: event.clientX,
      lastX: event.clientX,
    };
    try {
      event.currentTarget.setPointerCapture?.(event.pointerId);
    } catch (captureError) {
      // Older or simulated pointer events can fail capture; drag still works.
    }
  };

  const handleCertificationPointerMove = (event) => {
    const dragState = certificationDragRef.current;
    if (dragState.pointerId !== event.pointerId) return;

    const dragDistance = event.clientX - dragState.startX;
    dragState.lastX = event.clientX;

    if (Math.abs(dragDistance) > 8) {
      dragState.isDragging = true;
      event.currentTarget.classList.add("is-dragging");
      event.preventDefault();
    }
  };

  const handleCertificationPointerEnd = (event) => {
    const dragState = certificationDragRef.current;
    if (dragState.pointerId !== event.pointerId) return;

    const dragDistance = dragState.lastX - dragState.startX;
    const didDrag = dragState.isDragging && Math.abs(dragDistance) > 48;

    try {
      event.currentTarget.releasePointerCapture?.(event.pointerId);
    } catch (captureError) {
      // Ignore capture release failures from interrupted pointer sequences.
    }

    resetCertificationDrag(event.currentTarget);

    if (didDrag) {
      if (dragDistance < 0) {
        handleCertificationNext();
      } else {
        handleCertificationPrevious();
      }
    }
  };

  const handleCertificationPointerCancel = (event) => {
    resetCertificationDrag(event.currentTarget);
  };

  const handleFeaturedPrevious = () => {
    const previousIndex =
      activeFeaturedProject === 0
        ? featuredProjects.length - 1
        : activeFeaturedProject - 1;
    scrollToFeaturedProject(previousIndex);
  };

  const handleFeaturedNext = () => {
    const nextIndex =
      activeFeaturedProject === featuredProjects.length - 1
        ? 0
        : activeFeaturedProject + 1;
    scrollToFeaturedProject(nextIndex);
  };

  const handleMyWorkClick = () => {
    props.onNavigate("projects");
  };

  const handlePostsClick = () => {
    props.onNavigate("Blogs");
  };

  const handleResumeClick = () => {
    window.open(resumeDriveFolderUrl, "_blank", "noopener,noreferrer");
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleScheduleCall = () => {
    // Create Google Calendar event with Google Meet
    const now = new Date();
    const startDate = new Date(now.getTime() + 24 * 60 * 60 * 1000); // Tomorrow
    const endDate = new Date(startDate.getTime() + 30 * 60 * 1000); // 30 minutes later

    const formatDate = (date) => {
      return date.toISOString().replace(/[-:]/g, "").split(".")[0] + "Z";
    };

    const eventDetails = {
      text: "Technical Discussion with Vishnu Simha",
      dates: `${formatDate(startDate)}/${formatDate(endDate)}`,
      details: `Hi Vishnu! I'm interested in discussing a potential collaboration opportunity with you.

📋 Meeting Agenda:
• Introduction and project requirements
• Technical discussion and approach
• Timeline and next steps
• Q&A session

🔗 This meeting will include Google Meet for video conferencing.
📧 Meeting with: vishnusimha98@gmail.com

Looking forward to connecting with you!`,
      location: "Google Meet (link will be provided)",
      add: "vishnusimha98@gmail.com", // Add Vishnu as attendee
      src: "Google Meet", // This helps Google Calendar recognize it should add Meet
      trp: false, // Don't show guests in the event
    };

    const googleCalendarUrl = `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${encodeURIComponent(
      eventDetails.text,
    )}&dates=${eventDetails.dates}&details=${encodeURIComponent(
      eventDetails.details,
    )}&location=${encodeURIComponent(
      eventDetails.location,
    )}&add=${encodeURIComponent(eventDetails.add)}&src=${encodeURIComponent(
      eventDetails.src,
    )}&trp=${eventDetails.trp}`;

    window.open(googleCalendarUrl, "_blank");
  };

  const handleGitHubStatsError = useCallback((alt) => {
    setError(`Failed to load ${alt}`);
    console.error(`Failed to load GitHub stat: ${alt}`);
  }, []);

  const techStack = [
    "Kotlin",
    "Java",
    "Python",
    "Android",
    "Jetpack Compose",
    "Spring Boot/Microservices",
    "SQL",
    "AWS",
    "React/HTML/CSS",
    "Docker",
    "Kubernetes",
    "Github Actions",
    "Jenkins",
  ];

  const journeyMilestones = [
    {
      period: "2015 - 2020",
      title: "Engineering Roots",
      organization: "SRM University",
      location: "India",
      summary:
        "ECE foundations, Java, and early Android learning shaped my systems mindset.",
      tags: ["ECE", "Java", "Android Roots"],
      icon: <FaUniversity aria-hidden="true" />,
      tone: "lime",
    },
    {
      period: "2019 - 2022",
      title: "Enterprise Start",
      organization: "HID Global",
      location: "Chennai, India",
      summary:
        "Built secure mobile access features with BLE/NFC, SDK work, testing, and release support.",
      tags: ["BLE / NFC", "SDK", "Testing"],
      icon: <FaBriefcase aria-hidden="true" />,
      tone: "green",
    },
    {
      period: "2022",
      title: "Ownership Mode",
      organization: "HID Global",
      location: "Chennai, India",
      summary:
        "Owned Android delivery with MVVM, REST APIs, performance tuning, CI/CD, and quality gates.",
      tags: ["Kotlin", "REST APIs", "CI/CD"],
      icon: <FaCode aria-hidden="true" />,
      tone: "cyan",
    },
    {
      period: "2022 - 2023",
      title: "Ireland Chapter",
      organization: "Dublin City University",
      location: "Dublin, Ireland",
      summary:
        "Expanded into IoT, edge computing, Python Flask, data collection, and automation.",
      tags: ["IoT", "Python Flask", "Cloud"],
      icon: <FaGraduationCap aria-hidden="true" />,
      tone: "blue",
    },
    {
      period: "2024 - Present",
      title: "Builder Era",
      organization: "Independent Projects",
      location: "Ireland / India",
      summary:
        "Building practical products with Android apps, Spring Boot APIs, AWS workflows, and SaaS experiments.",
      tags: ["Spring Boot", "AWS", "SaaS"],
      icon: <FaRocket aria-hidden="true" />,
      tone: "violet",
    },
  ];

  const storyArcHighlights = [
    "System thinking",
    "Release ownership",
    "Product mindset",
  ];

  return (
    <div className="home-container">
      {/* Welcome Section */}
      <section ref={ref} className="home-welcome-section">
        <div className="home-welcome-content">
          <div className="home-welcome-text">
            <h1 className="home-greeting">Hi There!</h1>
            <h1 className="home-name">
              I'M <span className="highlight">Vishnu Simha</span>
            </h1>
            <h2 className="home-typing-text">
              <ReactTyped
                strings={[
                  "Software Engineer",
                  "Full stack Developer",
                  "Android Developer",
                  "AWS Solution Architect - Associate",
                ]}
                typeSpeed={30}
                backSpeed={10}
                loop
              />
            </h2>
            <p className="home-description">
              Android Engineer with 4+ years of experience building scalable
              mobile applications, backend systems, and cloud-integrated
              products using Kotlin, Spring Boot, AWS, Docker, and CI/CD.
            </p>

            <div
              className="hero-specialization-tags"
              aria-label="Core specializations"
            >
              <span>Android</span>
              <span>Backend</span>
              <span>AWS</span>
              <span>DevOps</span>
              <span>SaaS</span>
              <span>IoT</span>
            </div>

            {/* Call to Action Buttons - Updated Names */}
            <div className="cta-buttons">
              <button
                className="primary-btn"
                onClick={handleMyWorkClick} // Use the updated handler
              >
                My Work
              </button>
              <button
                className="secondary-btn"
                onClick={handlePostsClick} // Use the updated handler
              >
                Posts
              </button>
              <button
                className="secondary-btn resume-cta-btn"
                onClick={handleResumeClick}
              >
                <FaFileAlt aria-hidden="true" />
                Resume
              </button>
            </div>

            <div className="social-links">
              {socialLinks.map((link, index) => (
                <a
                  key={index}
                  href={link.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={link.label}
                  title={link.label}
                >
                  {link.icon}
                </a>
              ))}
            </div>
          </div>

          <div className="home-welcome-image">
            <Lottie
              animationData={codingAnimation}
              loop={true}
              autoplay={true}
              style={{ width: "100%", height: "100%" }}
            />
          </div>
        </div>
      </section>

      <section
        className="professional-snapshot-section"
        aria-label="Professional snapshot"
      >
        <div className="professional-snapshot-container">
          <div className="home-about-header snapshot-header">
            <h2>Professional Snapshot</h2>
            <div className="home-about-divider"></div>
          </div>

          <div className="snapshot-trust-grid">
            {trustHighlights.map((item) =>
              item.url ? (
                <a
                  className="trust-highlight-card trust-highlight-link-card"
                  key={item.label}
                  href={item.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={item.ariaLabel || `Open ${item.value}`}
                  title={item.ariaLabel || `Open ${item.value}`}
                >
                  <FaExternalLinkAlt
                    className="trust-card-external-icon"
                    aria-hidden="true"
                  />
                  <strong>{item.value}</strong>
                  <span>{item.label}</span>
                </a>
              ) : (
                <div className="trust-highlight-card" key={item.label}>
                  <strong>{item.value}</strong>
                  <span>{item.label}</span>
                </div>
              ),
            )}
          </div>

          <div className="snapshot-lower-grid">
            <div className="currently-building-card snapshot-card-block">
              <span className="currently-building-label">
                Currently building
              </span>
              <div className="currently-building-items">
                {currentFocusItems.map((item) => (
                  <span key={item}>→ {item}</span>
                ))}
              </div>
            </div>

            <div className="engineering-focus-compact">
              {engineeringFocus.map((focus) => (
                <article className="engineering-focus-card" key={focus.title}>
                  <h3>{focus.title}</h3>
                  <p>{focus.description}</p>
                </article>
              ))}
            </div>
          </div>

          <section
            className="aws-verified-badges-card"
            aria-labelledby="aws-verified-badges-title"
          >
            <div className="aws-badges-copy">
              <span className="currently-building-label">
                Verified credentials
              </span>
              <h3 id="aws-verified-badges-title">AWS Certification Badges</h3>
              <p>
                Official Credly badges for AWS Solutions Architect - Associate
                and AWS Cloud Practitioner.
              </p>
              <a
                className="aws-badges-profile-link"
                href={credlyUrl}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="View all Vishnu Simha Dussa Credly badges"
              >
                View all badges <FaExternalLinkAlt />
              </a>
            </div>

            <div className="aws-badges-grid">
              {awsCredentialBadges.map((badge) => (
                <CredlyBadge
                  key={badge.id}
                  badgeId={badge.id}
                  title={badge.title}
                />
              ))}
            </div>
          </section>
        </div>
      </section>

      {/* About Section */}
      <section className="home-about-section">
        <div className="home-about-container">
          <div className="home-about-header">
            <h2>About Me</h2>
            <div className="home-about-divider"></div>
          </div>

          <div className="home-about-content">
            <div className="home-about-text">
              <div className="about-intro-copy">
                <h3>Who am I?</h3>
                <p>
                  I'm a <strong>Software Engineer</strong> focused on{" "}
                  <strong>
                    Android, backend systems, and cloud-integrated product
                    development
                  </strong>
                  . My engineering journey began with electronics and IoT, but
                  over time I have built strong hands-on experience delivering
                  mobile applications, secure APIs, automation workflows, and
                  production-ready software.
                </p>
                <p>
                  I work across the full product lifecycle: requirements,
                  architecture, mobile experience, backend logic, cloud
                  deployment, testing, and release quality. My strength is
                  turning complex technical ideas into reliable products that
                  people can actually use.
                </p>
              </div>

              <div
                className="about-value-grid"
                aria-label="Team value and technical toolbox"
              >
                <div className="what-i-build-card about-product-flow-card">
                  <h4>What I bring to teams:</h4>
                  <div className="what-i-build-grid about-strength-grid">
                    <span>Mobile ownership</span>
                    <span>Backend thinking</span>
                    <span>Cloud awareness</span>
                    <span>Release discipline</span>
                  </div>
                </div>

                <div className="about-detail-card about-tech-stack-card">
                  <h4>Toolbox I bring:</h4>
                  <div className="tech-tags">
                    {techStack.map((tech, index) => (
                      <span key={index}>{tech}</span>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            <aside
              className="about-journey-visual"
              aria-labelledby="about-journey-title"
            >
              <div className="journey-panel">
                <div className="journey-panel-header">
                  <span className="journey-header-icon">
                    <FaRocket aria-hidden="true" />
                  </span>
                  <div>
                    <span className="section-eyebrow">Career Story</span>
                    <h3 id="about-journey-title">My Journey</h3>
                    <p>India → Ireland → mobile, backend, and cloud products</p>
                  </div>
                </div>

                <div
                  className="journey-route-strip journey-route-strip-expanded"
                  aria-label="India to Ireland to product builder journey route"
                >
                  <div className="journey-place-card journey-place-india">
                    <span className="journey-place-label">Where it began</span>
                    <span className="journey-place-skyline" aria-hidden="true">
                      ◢▁▃▅▃▁◣
                    </span>
                    <strong>🇮🇳 Tirupati / Chennai</strong>
                    <small>
                      Electronics roots, discipline, enterprise start
                    </small>
                  </div>

                  <div className="journey-flight-path" aria-hidden="true">
                    <span className="journey-flight-plane">
                      <FaPlane />
                    </span>
                  </div>

                  <div className="journey-place-card journey-place-ireland">
                    <span className="journey-place-label">
                      Where it expanded
                    </span>
                    <span className="journey-place-skyline" aria-hidden="true">
                      ▄▆█▆▄▂▆█
                    </span>
                    <strong>🇮🇪 Dublin, Ireland</strong>
                    <small>MSc mindset, IoT research, global exposure</small>
                  </div>

                  <div
                    className={`journey-place-card journey-place-product ${
                      showJourneyStickman
                        ? "is-stickman-visible"
                        : "is-stickman-hidden"
                    }`}
                    role="button"
                    tabIndex={0}
                    aria-pressed={showJourneyStickman}
                    aria-label={
                      showJourneyStickman
                        ? "Hide Product Builder stickman animation"
                        : "Show Product Builder stickman animation"
                    }
                    onClick={toggleJourneyStickman}
                    onKeyDown={handleJourneyStickmanKeyDown}
                  >
                    {showJourneyStickman && (
                      <div
                        className="journey-stickman-animation"
                        aria-hidden="true"
                      >
                        <span className="journey-stickman-hi">Hi</span>
                        <span className="journey-stickman-head"></span>
                        <span className="journey-stickman-body"></span>
                        <span className="journey-stickman-arm journey-stickman-arm-left"></span>
                        <span className="journey-stickman-arm journey-stickman-arm-right"></span>
                        <span className="journey-stickman-leg journey-stickman-leg-left"></span>
                        <span className="journey-stickman-leg journey-stickman-leg-right"></span>
                        <span className="journey-stickman-flag">
                          <span className="journey-stickman-flag-pole"></span>
                          <span className="journey-stickman-flag-cloth">
                            <span className="flag-saffron"></span>
                            <span className="flag-white">✺</span>
                            <span className="flag-green"></span>
                          </span>
                        </span>
                      </div>
                    )}
                    <span className="journey-place-label">
                      Where it is going
                    </span>
                    <span
                      className="journey-product-orbit"
                      aria-hidden="true"
                    ></span>
                    <strong>Product Builder</strong>
                    <small>Mobile apps, Spring Boot APIs, AWS delivery</small>
                  </div>
                </div>

                <div className="journey-timeline" role="list">
                  {journeyMilestones.map((milestone) => (
                    <article
                      className={`journey-milestone journey-tone-${milestone.tone}`}
                      key={`${milestone.period}-${milestone.title}`}
                      role="listitem"
                    >
                      <div className="journey-node">
                        <span>{milestone.icon}</span>
                      </div>

                      <div className="journey-milestone-card">
                        <span className="journey-period">
                          {milestone.period}
                        </span>
                        <h4>{milestone.title}</h4>
                        <strong>{milestone.organization}</strong>
                        <p>{milestone.location}</p>
                        <small>{milestone.summary}</small>
                        <div
                          className="journey-tag-row"
                          aria-label={`${milestone.title} key skills`}
                        >
                          {milestone.tags.map((tag) => (
                            <span key={tag}>{tag}</span>
                          ))}
                        </div>
                      </div>
                    </article>
                  ))}
                </div>

                <div
                  className="journey-story-card"
                  aria-label="Story arc and engineering approach"
                >
                  <div className="journey-story-copy">
                    <span className="section-eyebrow">Story Arc</span>
                    <h4>
                      From electronics roots to full-stack product engineering
                    </h4>
                    <p>
                      A career path across electronics, Android, IoT, backend,
                      and AWS cloud — shaped into one product engineering lens.
                    </p>
                  </div>

                  <div className="journey-story-steps journey-story-pills">
                    {storyArcHighlights.map((highlight) => (
                      <span key={highlight}>
                        <strong>✓</strong>
                        {highlight}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </aside>
          </div>
        </div>
      </section>

      <section
        className="featured-project-section"
        aria-label="Featured projects"
      >
        <div className="featured-project-shell">
          <div className="home-about-header featured-project-header">
            <span className="section-eyebrow">Featured Work</span>
            <h2>Production-Ready Projects</h2>
            <div className="home-about-divider"></div>
          </div>

          <div className="featured-carousel-frame">
            <button
              className="featured-carousel-control featured-carousel-control-left"
              type="button"
              onClick={handleFeaturedPrevious}
              aria-label="Show previous featured project"
            >
              <FaChevronLeft aria-hidden="true" />
            </button>

            <div
              className="featured-project-scroll"
              ref={featuredScrollRef}
              onScroll={handleFeaturedScroll}
              tabIndex="0"
              aria-label="Scrollable featured projects"
            >
              {featuredProjects.map((project, index) => (
                <article
                  className="featured-project-container"
                  key={project.title}
                  aria-current={
                    activeFeaturedProject === index ? "true" : undefined
                  }
                >
                  <div className="featured-project-copy">
                    <h2>{project.title}</h2>
                    <h3>{project.subtitle}</h3>
                    <p>{project.description}</p>
                    <ul className="featured-project-highlights">
                      {project.highlights.map((highlight) => (
                        <li key={highlight}>{highlight}</li>
                      ))}
                    </ul>
                    <div className="featured-project-actions">
                      <a
                        className="primary-btn"
                        href={project.primaryAction.url}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {project.primaryAction.label}
                      </a>
                      {project.secondaryAction.type === "projects" ? (
                        <button
                          className="secondary-btn"
                          onClick={handleMyWorkClick}
                        >
                          {project.secondaryAction.label}
                        </button>
                      ) : (
                        <a
                          className="secondary-btn"
                          href={project.secondaryAction.url}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {project.secondaryAction.label}
                        </a>
                      )}
                    </div>
                  </div>
                  <div className="featured-project-stack">
                    <h3>Stack & Scope</h3>
                    <div className="featured-stack-tags">
                      {project.stack.map((item) => (
                        <span key={item}>{item}</span>
                      ))}
                    </div>
                    <div className="featured-project-proof">
                      <strong>Why it matters</strong>
                      <p>{project.proof}</p>
                    </div>
                  </div>
                </article>
              ))}
            </div>

            <button
              className="featured-carousel-control featured-carousel-control-right"
              type="button"
              onClick={handleFeaturedNext}
              aria-label="Show next featured project"
            >
              <FaChevronRight aria-hidden="true" />
            </button>
          </div>

          <div
            className="featured-carousel-dots"
            aria-label="Featured project navigation"
          >
            {featuredProjects.map((project, index) => (
              <button
                key={project.title}
                className={`featured-carousel-dot ${
                  activeFeaturedProject === index ? "active" : ""
                }`}
                type="button"
                onClick={() => scrollToFeaturedProject(index)}
                aria-label={`Show ${project.title}`}
                aria-current={
                  activeFeaturedProject === index ? "true" : undefined
                }
              />
            ))}
          </div>
        </div>
      </section>

      {/* GitHub Stats Section - Third Half */}
      <section className="home-github-section">
        <div className="home-github-container">
          <div className="home-github-header">
            <h2>GitHub Stats</h2>
            <div className="home-github-divider"></div>
          </div>

          <div className="github-stats-content">
            {error && (
              <div className="error-message" role="alert" aria-live="polite">
                <span>⚠️ {error}</span>
                <button
                  onClick={() => setError(null)}
                  aria-label="Dismiss error message"
                  className="error-dismiss-btn"
                >
                  ×
                </button>
              </div>
            )}
            <div className="github-stats-header">
              <h4>Real-time Coding Activity:</h4>
              <div className="github-controls">
                <div className="current-date">
                  <FaCalendarAlt aria-hidden="true" />
                  <span>
                    {new Date().toLocaleDateString("en-US", {
                      weekday: "short",
                      year: "numeric",
                      month: "short",
                      day: "numeric",
                    })}
                  </span>
                </div>
                <button
                  className="refresh-stats-btn"
                  onClick={handleRefreshStats}
                  disabled={isRefreshing}
                  title="Refresh GitHub Stats"
                  aria-label="Refresh GitHub statistics"
                  aria-describedby="refresh-status"
                >
                  <FaSync
                    className={isRefreshing ? "spinning" : ""}
                    aria-hidden="true"
                  />
                  <span className="sr-only" id="refresh-status">
                    {isRefreshing
                      ? "Refreshing statistics..."
                      : "Click to refresh"}
                  </span>
                </button>
              </div>
            </div>
            <div className="github-stats-grid">
              <GitHubStatsCard
                src={`${GITHUB_README_STATS_BASE_URL}/api?${githubStatsQuery}&v=${refreshKey}`}
                fallbackSrc={`${GITHUB_README_STATS_FALLBACK_URL}/api?${githubStatsQuery}&v=${refreshKey}`}
                alt="GitHub Profile Stats"
                title="View my GitHub profile stats"
                href="https://github.com/Vishnusimha"
                isRefreshing={isRefreshing}
                onLoadError={handleGitHubStatsError}
              />
              <GitHubStatsCard
                src={`${GITHUB_README_STATS_BASE_URL}/api/top-langs/?${githubTopLanguagesQuery}&v=${refreshKey}`}
                fallbackSrc={`${GITHUB_README_STATS_FALLBACK_URL}/api/top-langs/?${githubTopLanguagesQuery}&v=${refreshKey}`}
                alt="GitHub Top Languages"
                title="View my GitHub languages"
                href="https://github.com/Vishnusimha"
                isRefreshing={isRefreshing}
                onLoadError={handleGitHubStatsError}
              />
              <GitHubStatsCard
                src={`https://github-readme-streak-stats.herokuapp.com/?user=Vishnusimha&theme=dark&hide_border=false&v=${refreshKey}`}
                alt="GitHub Streak Stats"
                title="View my GitHub streak"
                href="https://github.com/Vishnusimha"
                isRefreshing={isRefreshing}
                onLoadError={handleGitHubStatsError}
              />
              <GitHubStatsCard
                src={`https://github-readme-activity-graph.vercel.app/graph?username=Vishnusimha&theme=github-dark&hide_border=false&area=true&custom_title=GitHub%20Activity%20Graph&v=${refreshKey}`}
                alt="GitHub Activity Graph"
                title="View my GitHub activity graph"
                href="https://github.com/Vishnusimha"
                className="activity-graph-card"
                isRefreshing={isRefreshing}
                onLoadError={handleGitHubStatsError}
              />
            </div>
          </div>
        </div>
      </section>

      <section
        className="certifications-section"
        aria-labelledby="certifications-title"
      >
        <div className="certifications-container">
          <div className="home-github-header certifications-header">
            <span className="section-eyebrow">Proof of Learning</span>
            <h2 id="certifications-title">
              Certifications &amp; Professional Development
            </h2>
            <div className="home-github-divider"></div>
          </div>

          <div
            className="certification-tabs"
            role="tablist"
            aria-label="Filter certifications by type"
          >
            {certificationTabs.map((tab) => (
              <button
                className={`certification-tab ${
                  activeCertificationCategory === tab.id ? "active" : ""
                }`}
                type="button"
                key={tab.id}
                role="tab"
                aria-selected={
                  activeCertificationCategory === tab.id ? "true" : "false"
                }
                onClick={() => handleCertificationCategoryChange(tab.id)}
              >
                {tab.label}
              </button>
            ))}
          </div>

          <div className="certifications-content">
            <div
              className={`certifications-carousel-frame ${
                shouldPaginateCertifications ? "is-paginated" : ""
              }`}
              onPointerDown={handleCertificationPointerDown}
              onPointerMove={handleCertificationPointerMove}
              onPointerUp={handleCertificationPointerEnd}
              onPointerCancel={handleCertificationPointerCancel}
              onLostPointerCapture={handleCertificationPointerCancel}
            >
            {shouldPaginateCertifications && (
              <button
                className="featured-carousel-control featured-carousel-control-left certifications-carousel-control certifications-carousel-control-left"
                type="button"
                onClick={handleCertificationPrevious}
                aria-label="Show previous certifications"
              >
                <FaChevronLeft aria-hidden="true" />
              </button>
            )}

            <div
              className={`certifications-carousel-scroll ${
                shouldPaginateCertifications ? "is-paginated" : ""
              }`}
              ref={certificationsScrollRef}
              onScroll={
                shouldPaginateCertifications
                  ? handleCertificationScroll
                  : undefined
              }
              tabIndex={shouldPaginateCertifications ? "0" : undefined}
              aria-label={`${certificationTabs.find((tab) => tab.id === activeCertificationCategory)?.label} certification pages`}
            >
              {certificationPages.map((certificationPage, pageIndex) => (
                <div
                  className="certifications-page"
                  key={`certification-page-${pageIndex}`}
                  aria-current={
                    activeCertificationPage === pageIndex ? "true" : undefined
                  }
                >
                  <div className="certifications-grid">
                    {certificationPage.map((certification) => {
                      const certificationImage = getCertificationAsset(
                        certificationImageContext,
                        certification.imageFilename,
                      );
                      const shouldShowCertificationImage =
                        certificationImage &&
                        !failedCertificationImages[certification.title];
                      const localPdfUrl = getCertificationAsset(
                        certificationPdfContext,
                        certification.localPdfFilename,
                      );

                      return (
                        <article
                          className="certification-card"
                          key={certification.title}
                        >
                          <div className="certification-image-wrap">
                            {shouldShowCertificationImage ? (
                              <img
                                className="certification-image"
                                src={certificationImage}
                                alt={`${certification.title} certificate issued by ${certification.issuer}`}
                                loading="lazy"
                                draggable="false"
                                onError={() =>
                                  handleCertificationImageError(
                                    certification.title,
                                  )
                                }
                              />
                            ) : (
                              <div
                                className="certification-image-placeholder"
                                role="img"
                                aria-label={`${certification.title} certificate thumbnail from ${certification.issuer} unavailable`}
                              >
                                <FaCertificate aria-hidden="true" />
                                <span>{certification.issuer}</span>
                              </div>
                            )}
                          </div>

                          <div className="certification-content">
                            <p className="certification-meta">
                              {certification.issuer} ·{" "}
                              {certification.issuedDate}
                            </p>
                            <h3 className="certification-title">
                              {certification.title}
                            </h3>
                            {certification.description && (
                              <p className="certification-description">
                                {certification.description}
                              </p>
                            )}

                            {(certification.type ||
                              certification.skills?.length > 0) && (
                              <div
                                className="certification-skills"
                                aria-label={`${certification.title} credential badges`}
                              >
                                {certification.type && (
                                  <span
                                    className={`certification-skill certification-type-badge certification-type-badge-${certification.category}`}
                                  >
                                    {certification.type}
                                  </span>
                                )}
                                {certification.skills?.map((skill) => (
                                  <span
                                    className="certification-skill"
                                    key={skill}
                                  >
                                    {skill}
                                  </span>
                                ))}
                              </div>
                            )}

                            <div className="certification-links">
                              {certification.credentialUrl && (
                                <a
                                  className="certification-link"
                                  href={certification.credentialUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                >
                                  Verify credential <FaExternalLinkAlt />
                                </a>
                              )}
                              {localPdfUrl && (
                                <a
                                  className="certification-link certification-link-secondary"
                                  href={localPdfUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                >
                                  View certificate <FaExternalLinkAlt />
                                </a>
                              )}
                            </div>
                          </div>
                        </article>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>

            {shouldPaginateCertifications && (
              <button
                className="featured-carousel-control featured-carousel-control-right certifications-carousel-control certifications-carousel-control-right"
                type="button"
                onClick={handleCertificationNext}
                aria-label="Show next certifications"
              >
                <FaChevronRight aria-hidden="true" />
              </button>
            )}
            </div>

            {showCertificationProgressNote && (
              <p className="certifications-progress-note">
                {certificationProgressNote}
              </p>
            )}

            {shouldPaginateCertifications && (
              <div
                className="featured-carousel-dots certifications-carousel-dots"
                aria-label="Certifications navigation"
              >
                {certificationPages.map((certificationPage, pageIndex) => (
                  <button
                    key={`certification-dot-${pageIndex}`}
                    className={`featured-carousel-dot ${
                      activeCertificationPage === pageIndex ? "active" : ""
                    }`}
                    type="button"
                    onClick={() => scrollToCertificationPage(pageIndex)}
                    aria-label={`Show certifications page ${pageIndex + 1} of ${
                      certificationPages.length
                    }`}
                    aria-current={
                      activeCertificationPage === pageIndex ? "true" : undefined
                    }
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Footer Section */}
      <footer className="home-footer">
        {/* CTA Section */}
        <div className="footer-cta-section">
          <div className="footer-cta-container">
            <div className="footer-cta-content">
              <h2>Let's Build Something Amazing Together</h2>
              <p>
                Ready to turn your ideas into scalable, high-performance
                applications?
              </p>
              <div className="footer-cta-buttons">
                <button
                  className="footer-primary-btn"
                  onClick={() =>
                    window.open(
                      "mailto:vishnusimha98@gmail.com?subject=Project Collaboration&body=Hi Vishnu, I would like to discuss a project opportunity with you.",
                      "_blank",
                    )
                  }
                >
                  <FaEnvelope />
                  Start a Project
                </button>
                <button
                  className="footer-secondary-btn"
                  onClick={handleScheduleCall}
                >
                  <FaVideo />
                  Schedule Call
                </button>
              </div>
            </div>
            <div className="footer-cta-status">
              <div className="status-indicator">
                <div className="status-dot"></div>
                <span>
                  <strong>Available for new opportunities</strong>
                </span>
              </div>
              <div className="coffee-chat">
                <FaCoffee style={{ fontSize: "1.2em" }} />
                <span>Always up for a coffee chat about tech!</span>
              </div>
            </div>
          </div>
        </div>

        {/* Main Footer Content */}
        <div className="footer-main">
          <div className="footer-container">
            <div className="footer-grid">
              {/* About Column */}
              <div className="footer-column">
                <h3>Vishnu Simha</h3>
                <p className="footer-description">
                  Android-focused Software Engineer specializing in mobile,
                  backend, AWS, and product-ready full stack systems.
                </p>
                <div className="footer-location">
                  <FaMapMarkerAlt />
                  <span>Based in 🇮🇳India/🇮🇪Ireland</span>
                </div>
                <div className="footer-social-links">
                  {socialLinks.map((link, index) => (
                    <a
                      key={index}
                      href={link.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={link.label}
                      title={link.label}
                      className="footer-social-link"
                    >
                      {link.icon}
                    </a>
                  ))}
                </div>
              </div>

              {/* Quick Links Column */}
              <div className="footer-column">
                <h4>Quick Links</h4>
                <ul className="footer-links">
                  <li>
                    <a href="#home" onClick={() => scrollToTop()}>
                      Home
                    </a>
                  </li>
                  <li>
                    <a href="#about" onClick={() => props.onNavigate("home")}>
                      About
                    </a>
                  </li>
                  <li>
                    <a
                      href="#projects"
                      onClick={() => props.onNavigate("projects")}
                    >
                      Projects
                    </a>
                  </li>
                  <li>
                    <a href="#blog" onClick={() => props.onNavigate("Blogs")}>
                      Blog
                    </a>
                  </li>
                  <li>
                    <a
                      href={resumeDriveFolderUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Resume
                    </a>
                  </li>
                  <li>
                    <a
                      href="#contact"
                      onClick={() => props.onNavigate("contact")}
                    >
                      Contact
                    </a>
                  </li>
                </ul>
              </div>

              {/* Services Column */}
              <div className="footer-column">
                <h4>Services</h4>
                <ul className="footer-links">
                  <li>Android App Development</li>
                  <li>Full Stack Web Development</li>
                  <li>IoT Solutions</li>
                  <li>Technical Consulting</li>
                </ul>
              </div>

              {/* Technologies Column */}
              <div className="footer-column">
                <h4>Technologies</h4>
                <div className="footer-tech-grid">
                  <span className="footer-tech-tag">Kotlin</span>
                  <span className="footer-tech-tag">Java</span>
                  <span className="footer-tech-tag">Android</span>
                  <span className="footer-tech-tag">Spring Boot</span>
                </div>
                <div className="footer-experience">
                  <div className="experience-item">
                    <strong>4+</strong>
                    <span>Years Experience</span>
                  </div>
                  <div className="experience-item">
                    <strong>20+</strong>
                    <span>Projects Delivered</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Footer Bottom */}
        <div className="footer-bottom">
          <div className="footer-container">
            <div className="footer-bottom-content">
              <div className="footer-copyright">
                <p>
                  © {new Date().getFullYear()} Vishnu Simha. All rights
                  reserved.
                </p>
                <p className="footer-built-with">
                  Built with <FaHeart className="heart-icon" /> using <FaCode />{" "}
                  React & deployed on Vercel
                </p>
              </div>
              <div className="footer-bottom-right">
                <div className="footer-last-updated">
                  Last updated:{" "}
                  {new Date().toLocaleDateString("en-US", {
                    month: "short",
                    day: "numeric",
                    year: "numeric",
                  })}
                </div>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
});

export default Home;
